import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getExpenses, getExpenseCategories, postExpenseJson, postExpenseMultipart,
  postExpenseCategory, approveExpense, rejectExpense, putExpense,
  getOwnerWalletBanks, postOwnerWalletResolveAccount, postOwnerWalletTransfer,
  getOwnerWallet
} from '@/api/owner';
import { FileText, AlertCircle, CheckCircle2, Plus, X, RefreshCw, Edit2, ShieldCheck } from 'lucide-react';

type Tab = 'all' | 'general' | 'salaries';

const unwrap = (r: any) => r?.data?.data ?? r?.data;
const arr = <T,>(s: any): T[] => {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  if (Array.isArray(s.data)) return s.data;
  if (Array.isArray(s.data?.data)) return s.data.data;
  return [];
};
const money = (n: any) => n == null ? '—' : '₦' + Number(n).toLocaleString('en-NG', { maximumFractionDigits: 2 });
const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const statusTone: Record<string, string> = {
  paid: 'bg-green-50 text-green-700 border-green-200',
  pending_approval: 'bg-amber-50 text-amber-800 border-amber-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

export function ExpensesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showNewExpense, setShowNewExpense] = useState(false);
  const [payingExpense, setPayingExpense] = useState<any | null>(null);
  const [editingExpense, setEditingExpense] = useState<any | null>(null);
  
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');

  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(t);
  }, [error]);

  const expensesQ = useQuery({
    queryKey: ['owner-expenses', statusFilter, categoryFilter, fromFilter, toFilter],
    queryFn: () => getExpenses({
      status: statusFilter || undefined,
      category_id: categoryFilter || undefined,
      from: fromFilter || undefined,
      to: toFilter || undefined,
    }).then(unwrap),
  });
  const categoriesQ = useQuery({ queryKey: ['owner-expense-categories'], queryFn: () => getExpenseCategories().then(unwrap) });

  const expenses = arr<any>(expensesQ.data?.data ?? expensesQ.data);
  const categories = arr<any>(categoriesQ.data);

  const filteredExpenses = useMemo(() => {
    return expenses.filter((e: any) => {
      if (tab === 'general') return e.vendor !== 'Staff Payroll';
      if (tab === 'salaries') return e.vendor === 'Staff Payroll';
      return true;
    });
  }, [expenses, tab]);

  const stats = useMemo(() => {
    const approved = filteredExpenses.filter((e: any) => ['approved', 'paid'].includes(e.status));
    const pending = filteredExpenses.filter((e: any) => e.status === 'pending_approval');
    const paidThisPeriod = filteredExpenses.filter((e: any) => e.status === 'paid');
    return {
      approvedTotal: approved.reduce((sum: number, e: any) => sum + Number(e.amount || 0), 0),
      pendingCount: pending.length,
      paidCount: paidThisPeriod.length,
      categories: new Set(filteredExpenses.map((e: any) => e.category).filter(Boolean)).size,
    };
  }, [filteredExpenses]);

  const createCategory = async (name: string, kind: 'operating' | 'capital' | 'salary' | 'other') => {
    const res = await postExpenseCategory({ name, kind });
    const d = unwrap(res);
    categoriesQ.refetch();
    return d;
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-[100] max-w-md">
          <div className="bg-red-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-semibold flex-1">{error}</p>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {successMsg && (
        <div className="fixed top-4 right-4 z-[100] max-w-md">
          <div className="bg-green-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-semibold flex-1">{successMsg}</p>
            <button onClick={() => setSuccessMsg(null)}><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">FINANCE</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Expenses</h1>
          <p className="text-slate-500 text-sm mt-1">Record school expenses · owner's entries are paid immediately, others need approval</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => expensesQ.refetch()} className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={() => setShowNewExpense(true)} className="px-4 py-2 text-sm font-bold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1.5 shadow-sm">
            <Plus className="w-4 h-4" /> Record expense
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={<FileText className="w-4 h-4" />} label="Total approved" value={money(stats.approvedTotal)} tone="bg-blue-50 text-blue-600" />
        <StatCard icon={<AlertCircle className="w-4 h-4" />} label="Pending approval" value={String(stats.pendingCount)} tone="bg-amber-50 text-amber-600" />
        <StatCard icon={<CheckCircle2 className="w-4 h-4" />} label="Paid this period" value={String(stats.paidCount)} tone="bg-green-50 text-green-600" />
        <StatCard icon={<FileText className="w-4 h-4" />} label="Categories" value={String(stats.categories)} tone="bg-violet-50 text-violet-600" />
      </div>

      <div className="flex border-b border-slate-200 gap-2">
        <button onClick={() => setTab('all')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors ${tab === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>All Expenses</button>
        <button onClick={() => setTab('general')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors ${tab === 'general' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>General Expenses</button>
        <button onClick={() => setTab('salaries')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors ${tab === 'salaries' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Salaries</button>
      </div>

      <div className="bg-white p-4 rounded-xl border border-slate-200 flex flex-wrap gap-3 items-end shadow-sm">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All</option>
            <option value="pending_approval">Pending</option>
            <option value="paid">Paid</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Category</label>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All</option>
            {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">From</label>
          <input type="date" value={fromFilter} onChange={(e) => setFromFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">To</label>
          <input type="date" value={toFilter} onChange={(e) => setToFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      {expensesQ.isLoading ? (
        <p className="text-sm text-slate-500 text-center py-8">Loading expenses…</p>
      ) : filteredExpenses.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No expenses recorded</p>
          <p className="text-sm text-slate-500 mt-1">Record generator fuel, cleaning supplies, repairs, and other school costs here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Title</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-left">Vendor</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredExpenses.map((e: any) => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(e.spent_on)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{e.title}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{e.category ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{e.vendor ?? '—'}</td>
                  <td className="px-4 py-3 text-xs capitalize text-slate-600">{e.payment_method}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{money(e.amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${statusTone[e.status] ?? 'bg-slate-100 text-slate-600'}`}>
                      {String(e.status ?? '').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {e.status === 'pending_approval' && (
                      <button
                        onClick={() => setPayingExpense(e)}
                        className="px-3 py-1.5 bg-[#0f172a] text-white rounded text-xs font-bold hover:bg-slate-800 mr-2"
                      >
                        Pay Now
                      </button>
                    )}
                    {e.status === 'paid' && <span className="text-xs text-green-600 font-semibold mr-2">✓ Paid</span>}
                    {e.status === 'rejected' && <span className="text-xs text-red-500 font-semibold mr-2">Rejected</span>}
                    
                    <button
                      onClick={() => setEditingExpense(e)}
                      className="text-xs font-bold text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNewExpense && (
        <NewExpenseModal
          categories={categories}
          onCreateCategory={createCategory}
          onClose={() => setShowNewExpense(false)}
          onSuccess={(m?: string) => { setShowNewExpense(false); expensesQ.refetch(); setSuccessMsg(m ?? 'Expense recorded.'); }}
          onError={(m) => setError(m)}
        />
      )}

      {payingExpense && (
        <PayExpenseModal
          expense={payingExpense}
          onClose={() => setPayingExpense(null)}
          onSuccess={(m?: string) => {
            setPayingExpense(null);
            setSuccessMsg(m ?? 'Expense paid successfully.');
            expensesQ.refetch();
          }}
          onError={(m) => setError(m)}
        />
      )}

      {editingExpense && (
        <EditExpenseModal
          expense={editingExpense}
          categories={categories}
          onClose={() => setEditingExpense(null)}
          onSuccess={() => {
            setEditingExpense(null);
            setSuccessMsg('Expense updated.');
            expensesQ.refetch();
          }}
          onError={(m) => setError(m)}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string; tone: string }) {
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg mb-2 ${tone}`}>
        {icon}
      </div>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

/* ─── BANK SELECTOR COMPONENT (From Wallet UI) ─── */
function BankSelector({ bankCode, setBankCode }: { bankCode: string, setBankCode: (v: string) => void }) {
  const [search, setSearch] = useState('');
  const [letter, setLetter] = useState('ALL');

  const banksQ = useQuery({ queryKey: ['owner-wallet-banks'], queryFn: () => getOwnerWalletBanks().then(unwrap) });
  const banks = arr<any>(banksQ.data);

  const filtered = banks.filter((b: any) => {
    const matchSearch = b.name.toLowerCase().includes(search.toLowerCase()) || b.code.includes(search);
    const matchLetter = letter === 'ALL' || b.name.toUpperCase().startsWith(letter);
    return matchSearch && matchLetter;
  });

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-slate-700">Bank *</label>
      <input 
        value={search} 
        onChange={(e) => setSearch(e.target.value)} 
        placeholder="Search bank name or code" 
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2" 
      />
      <div className="flex flex-wrap gap-0.5 mb-2 select-none">
        <button type="button" onClick={() => setLetter('')} className={`px-2 py-0.5 text-xs font-bold rounded ${letter === '' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>ALL</button>
        {['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'].map(L => (
          <button key={L} type="button" onClick={() => setLetter(L)} className={`w-6 h-6 text-xs font-bold rounded ${letter === L ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
            {L}
          </button>
        ))}
      </div>
      <select value={bankCode} onChange={(e) => setBankCode(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
        <option value="">{filtered.length === 0 ? 'No banks match' : `Choose from ${filtered.length} banks`}</option>
        {filtered.map((b: any) => <option key={b.code} value={b.code}>{b.name} ({b.code})</option>)}
      </select>
    </div>
  );
}

/* ─── NEW EXPENSE MODAL ─── */
function NewExpenseModal({
  categories, onCreateCategory, onClose, onSuccess, onError,
}: {
  categories: any[];
  onCreateCategory: (name: string, kind: 'operating' | 'capital' | 'salary' | 'other') => Promise<any>;
  onClose: () => void;
  onSuccess: (msg?: string) => void;
  onError: (msg: string) => void;
}) {
  const [mode, setMode] = useState<'record' | 'pay'>('record');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>(categories[0]?.id ?? '');
  const [spentOn, setSpentOn] = useState(new Date().toISOString().slice(0, 10));
  const [vendor, setVendor] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Wallet Payment state
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);

  const walletQ = useQuery({ queryKey: ['owner-wallet'], queryFn: () => getOwnerWallet().then(unwrap) });
  const walletBalance = walletQ.data?.balance ?? 0;

  const verify = async (acct: string) => {
    if (acct.length === 10 && bankCode) {
      setVerifying(true);
      try {
        const r: any = await postOwnerWalletResolveAccount({ bank_code: bankCode, account_number: acct });
        setAccountName(r.data?.data?.account_name ?? r.data?.account_name);
      } catch (e) { onError('Could not verify account'); }
      finally { setVerifying(false); }
    }
  };

  const submit = async () => {
    if (!title.trim()) return onError('Title required');
    const amt = Number(amount);
    if (!amt || amt < 1) return onError('Amount must be greater than 0');
    if (!categoryId) return onError('Pick a category');
    if (!spentOn) return onError('Date required');
    
    if (mode === 'pay') {
      if (!bankCode || !accountNumber || !accountName) return onError('Complete bank details first');
      if (!pin) return onError('PIN is required');
      if (amt > walletBalance) return onError('Amount exceeds available wallet balance');
    }

    setSubmitting(true);
    try {
      let note: string | undefined;
      if (mode === 'pay') {
        // 1. Record the expense, 2. send the money against it — the server marks it paid once the money goes
        //    (or keeps it waiting if the owner approves the bursar's payments).
        const created: any = await postExpenseJson({
          expense_category_id: Number(categoryId), title: title.trim(), amount: amt, spent_on: spentOn,
          vendor: vendor.trim() || null, payment_method: 'wallet', notes: notes.trim() || null,
        });
        const expenseId = created?.data?.data?.id ?? created?.data?.id;
        const idempotencyKey = 'exp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
        const t: any = await postOwnerWalletTransfer({
          amount: amt, bank_code: bankCode, account_number: accountNumber, account_name: accountName,
          narration: `Expense: ${title}`.slice(0, 60), purpose: title.trim(), expense_id: expenseId,
        }, pin, idempotencyKey);
        note = t?.status === 202 ? t?.data?.message : 'Expense recorded and paid.';
      } else {
        // Cash: paid at once when you may pay; otherwise it waits for the owner
        const r: any = await postExpenseJson({
          expense_category_id: Number(categoryId), title: title.trim(), amount: amt, spent_on: spentOn,
          vendor: vendor.trim() || null, payment_method: 'cash', notes: notes.trim() || null,
        });
        note = r?.data?.message;
      }
      onSuccess(note);
    } catch (e: any) {
      const msg = e?.response?.data?.message || (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' · ')) || 'Submit failed';
      onError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Record expense</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          
          <div className="flex border-b border-slate-200">
            <button onClick={() => setMode('record')} className={`flex-1 py-3 text-sm font-bold ${mode === 'record' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}>Record Paid (Cash)</button>
            <button onClick={() => setMode('pay')} className={`flex-1 py-3 text-sm font-bold ${mode === 'pay' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}>Pay via Wallet</button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Title *</label><input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. Generator repair" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Amount * (₦)</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="0.00" /></div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Date *</label><input type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Category *</label>
              <select value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value) || '')} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
                <option value="">Select…</option>
                {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Vendor</label><input value={vendor} onChange={(e) => setVendor(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. Bright Electricals" /></div>
            
            {mode === 'pay' && (
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                <p className="text-xs text-slate-500 bg-blue-50 p-2 rounded text-center">Available XpressWallet Balance: <b>{money(walletBalance)}</b></p>
                <BankSelector bankCode={bankCode} setBankCode={setBankCode} />
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Account Number *</label>
                  <div className="flex gap-2">
                    <input value={accountNumber} onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setAccountNumber(v); if (v.length === 10) verify(v); }} maxLength={10} className="flex-1 border border-slate-300 rounded-lg p-2.5 text-sm font-mono" placeholder="10 digits" />
                    <button onClick={() => verify(accountNumber)} className="px-4 py-2.5 bg-slate-200 text-slate-700 rounded-lg font-bold text-sm hover:bg-slate-300 disabled:opacity-50">Verify</button>
                  </div>
                  {verifying && <p className="text-xs text-blue-600 mt-1">Verifying…</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Account Name</label>
                  <input value={accountName} readOnly className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-slate-50" placeholder="Auto-filled" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Authorize with PIN *</label>
                  <input type="password" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm tracking-[0.5em] font-mono" placeholder="••••" />
                </div>
              </div>
            )}

            {mode === 'record' && (
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Notes</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            )}
          </div>

          <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <button onClick={submit} disabled={submitting} className="w-full py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800 disabled:opacity-50">
              {submitting ? 'Processing…' : mode === 'pay' ? 'Pay & Record Expense' : 'Record Expense'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── PAY EXPENSE MODAL (For Pending Approvals) ─── */
function PayExpenseModal({ expense, onClose, onSuccess, onError }: {
  expense: any; onClose: () => void; onSuccess: (msg?: string) => void; onError: (msg: string) => void;
}) {
  const [mode, setMode] = useState<'record' | 'pay'>('pay');
  const [submitting, setSubmitting] = useState(false);
  
  // Wallet state
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);

  const walletQ = useQuery({ queryKey: ['owner-wallet'], queryFn: () => getOwnerWallet().then(unwrap) });
  const walletBalance = walletQ.data?.balance ?? 0;

  const verify = async (acct: string) => {
    if (acct.length === 10 && bankCode) {
      setVerifying(true);
      try {
        const r: any = await postOwnerWalletResolveAccount({ bank_code: bankCode, account_number: acct });
        setAccountName(r.data?.data?.account_name ?? r.data?.account_name);
      } catch (e) { onError('Could not verify account'); }
      finally { setVerifying(false); }
    }
  };

  const submit = async () => {
    if (mode === 'pay') {
      if (!bankCode || !accountNumber || !accountName) return onError('Complete bank details first');
      if (!pin) return onError('PIN is required');
      if (Number(expense.amount) > walletBalance) return onError('Amount exceeds available wallet balance');
    }

    setSubmitting(true);
    try {
      if (mode === 'pay') {
        // 1. Send via Xpress
        const idempotencyKey = 'exp-pay-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
        const t: any = await postOwnerWalletTransfer({
          amount: Number(expense.amount),
          bank_code: bankCode,
          account_number: accountNumber,
          account_name: accountName,
          narration: `Expense: ${expense.title}`.slice(0, 60),
          purpose: expense.title, expense_id: expense.id,
        }, pin, idempotencyKey);
        // the server marks the expense paid when the money goes
        onSuccess(t?.status === 202 ? t?.data?.message : 'Expense paid.');
      } else {
        // paid in cash or from the school's bank outside the app: book it
        const r: any = await approveExpense(expense.id, undefined, { payment_method: 'cash' });
        onSuccess(r?.data?.message ?? 'Expense marked as paid.');
      }
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
          <h3 className="font-bold text-slate-900">Pay expense</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex border-b border-slate-200">
          <button onClick={() => setMode('record')} className={`flex-1 py-3 text-sm font-bold ${mode === 'record' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}>Mark as Paid</button>
          <button onClick={() => setMode('pay')} className={`flex-1 py-3 text-sm font-bold ${mode === 'pay' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}>Pay via Wallet</button>
        </div>
        
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-center mb-4">
            <p className="text-sm text-slate-500">Amount</p>
            <p className="text-3xl font-extrabold text-slate-900">{money(expense.amount)}</p>
            <p className="text-xs text-slate-500 mt-1">Title: {expense.title}</p>
          </div>

          {mode === 'pay' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500 bg-blue-50 p-2 rounded text-center">Available XpressWallet Balance: <b>{money(walletBalance)}</b></p>
              <BankSelector bankCode={bankCode} setBankCode={setBankCode} />
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Account Number *</label>
                <div className="flex gap-2">
                  <input value={accountNumber} onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setAccountNumber(v); if (v.length === 10) verify(v); }} maxLength={10} className="flex-1 border border-slate-300 rounded-lg p-2.5 text-sm font-mono" placeholder="10 digits" />
                  <button onClick={() => verify(accountNumber)} className="px-4 py-2.5 bg-slate-200 text-slate-700 rounded-lg font-bold text-sm hover:bg-slate-300 disabled:opacity-50">Verify</button>
                </div>
                {verifying && <p className="text-xs text-blue-600 mt-1">Verifying…</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Account Name</label>
                <input value={accountName} readOnly className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-slate-50" placeholder="Auto-filled" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Authorize with PIN *</label>
                <input type="password" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm tracking-[0.5em] font-mono" placeholder="••••" />
              </div>
            </div>
          )}

          {mode === 'record' && (
            <p className="text-sm text-slate-600 text-center py-4 bg-slate-50 rounded-lg border border-slate-200">
              Clicking confirm will mark this expense as manually paid without deducting from the XpressWallet.
            </p>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <button onClick={submit} disabled={submitting} className="w-full py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800 disabled:opacity-50">
            {submitting ? 'Processing…' : mode === 'pay' ? 'Pay from Wallet' : 'Mark as Paid'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── EDIT EXPENSE MODAL ─── */
function EditExpenseModal({ expense, categories, onClose, onSuccess, onError }: {
  expense: any; categories: any[]; onClose: () => void; onSuccess: () => void; onError: (msg: string) => void;
}) {
  const [title, setTitle] = useState(expense.title);
  const [amount, setAmount] = useState(String(expense.amount));
  const [categoryId, setCategoryId] = useState<number | ''>(expense.expense_category_id ?? '');
  const [status, setStatus] = useState(expense.status);
  const [vendor, setVendor] = useState(expense.vendor ?? '');
  const [submitting, setSubmitting] = useState(false);
  
  const canEditAmount = !(expense.status === 'paid' && expense.payment_method === 'wallet');

  const submit = async () => {
    if (!title.trim()) return onError('Title required');
    const amt = Number(amount);
    if (!amt || amt < 1) return onError('Amount must be greater than 0');
    if (!categoryId) return onError('Pick a category');

    setSubmitting(true);
    try {
      await putExpense(expense.id, {
        title: title.trim(),
        amount: amt,
        expense_category_id: Number(categoryId),
        status,
        vendor: vendor.trim() || null,
      });
      onSuccess();
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Edit expense</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-3">
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Title *</label><input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Amount * (₦)</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!canEditAmount} className={`w-full border border-slate-300 rounded-lg p-2.5 text-sm ${!canEditAmount ? 'bg-slate-100 cursor-not-allowed' : ''}`} />
                {!canEditAmount && <p className="text-[10px] text-red-500 font-semibold mt-1">Cannot edit amount for paid wallet transactions.</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
                  <option value="pending_approval">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Category *</label>
              <select value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value) || '')} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
                <option value="">Select…</option>
                {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Vendor</label><input value={vendor} onChange={(e) => setVendor(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <button onClick={submit} disabled={submitting} className="w-full py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800 disabled:opacity-50">
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
